import React, { useState } from 'react';
import { Search } from 'lucide-react';

interface QuickSelectOption {
  label: string;
  values: string[];
}

interface SearchableMultiSelectProps {
  label: string;
  placeholder: string;
  options: readonly string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  quickSelects?: QuickSelectOption[];
}

export const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  placeholder,
  options,
  selectedValues,
  onChange,
  quickSelects = [],
}) => {
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredOptions = options.filter(
    opt => !selectedValues.includes(opt) &&
      (search === '' || opt.toLowerCase().includes(search.toLowerCase()))
  );

  const addValue = (value: string) => {
    onChange([...selectedValues, value]);
    setSearch('');
  };

  const removeValue = (value: string) => {
    onChange(selectedValues.filter(v => v !== value));
  };

  const addMultiple = (values: string[]) => {
    onChange([...new Set([...selectedValues, ...values])]);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-2xs font-medium text-neutral-400 uppercase tracking-wide">{label}</span>
        {selectedValues.length > 0 && (
          <button
            onClick={() => onChange([])}
            className="text-2xs font-medium text-error-500 hover:text-error-600"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="relative mt-1.5">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400 pointer-events-none z-10" />
        <input
          type="text"
          value={search}
          placeholder={placeholder}
          className="w-full pl-8 pr-3 py-2 text-xs text-neutral-700 bg-white border border-neutral-200 rounded-lg focus:border-neutral-300 focus:ring-1 focus:ring-neutral-100 outline-none"
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        />
        {showDropdown && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-56 overflow-y-auto z-50">
            {quickSelects.length > 0 && (
              <div className="flex gap-1 p-2 border-b border-neutral-100">
                {quickSelects.map(qs => (
                  <button
                    key={qs.label}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addMultiple(qs.values);
                    }}
                    className="px-2 py-1 text-2xs font-medium text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded transition"
                  >
                    + {qs.label}
                  </button>
                ))}
              </div>
            )}
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-2xs text-neutral-400">No results found</div>
            ) : (
              filteredOptions.map(option => (
                <button
                  key={option}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addValue(option);
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-neutral-700 hover:bg-neutral-50 transition"
                >
                  {option}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedValues.map(value => (
            <span
              key={value}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-2xs font-medium text-neutral-600 bg-neutral-100 rounded"
            >
              {value}
              <button
                onClick={() => removeValue(value)}
                className="text-neutral-400 hover:text-neutral-600"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
